import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, Edit3, Hash, PackagePlus, Plus, Printer, RotateCcw } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { fmtMoney } from "../../lib/format";
import { INP } from "./shared";
import { ReceiptFormModal } from "./ReceiptFormModal";
import { EmailReceiptButton } from "./EmailReceiptButton";

function NextNumberBar({ next, reload }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const save = async () => {
    try { await api.put("/receipts/next-number", { next_number: parseInt(val, 10) }); toast.success("Next receipt number updated"); setEditing(false); reload(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-sm px-4 py-3" data-testid="receipt-next-number-bar">
      <Hash className="h-4 w-4 text-[#1E2A78]" />
      <span className="text-sm text-slate-600">Next receipt number:</span>
      <span className="font-mono font-bold text-red-700" data-testid="receipt-next-number">{next?.preview || "—"}</span>
      {editing ? (
        <>
          <input type="number" min="1" value={val} onChange={(e) => setVal(e.target.value)} className={`${INP} w-32`} data-testid="receipt-next-number-input" />
          <button onClick={save} className="text-xs px-3 py-1.5 bg-[#1E2A78] text-white rounded-sm" data-testid="receipt-next-number-save">Save</button>
          <button onClick={() => setEditing(false)} className="text-xs text-slate-500">Cancel</button>
        </>
      ) : (
        <button onClick={() => { setVal(String(next?.next_number || "")); setEditing(true); }} className="text-xs text-[#1E2A78] hover:underline" data-testid="receipt-next-number-edit">Change starting number</button>
      )}
    </div>
  );
}

const MODE_SERVICE = { Ocean: "Sea Freight", Air: "Air Freight", Express: "Courier" };

function shipmentPayload(r) {
  return {
    tracking_number: r.receipt_number, sender_name: r.shipper_name, sender_phone: r.shipper.phone || null,
    receiver_name: r.consignee_name, receiver_phone: r.consignee.phone || null,
    origin: r.origin || r.shipper.city || "Canada", destination: r.destination, service_type: MODE_SERVICE[r.mode] || "Sea Freight",
    description: r.items.map((i) => `${i.qty} × ${i.description || "package"}`).join(", "),
    weight_kg: r.total_weight_lbs ? Math.round(r.total_weight_lbs * 0.4536 * 10) / 10 : null,
    status: "At Origin Warehouse",
  };
}

function ReceiptRow({ r, tracked, onEdit, onChanged }) {
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(formatApiError(e)); } };
  const btn = "inline-flex items-center gap-1 text-xs hover:underline mr-3";
  return (
    <tr className={`border-t border-slate-100 ${r.voided ? "opacity-50" : ""}`} data-testid={`receipt-row-${r.receipt_number}`}>
      <td className="px-4 py-3 font-mono text-xs font-bold text-red-700">{r.receipt_number}{r.voided && <span className="ml-2 text-[10px] text-red-600 uppercase">void</span>}</td>
      <td className="px-4 py-3 text-xs">{r.date}</td>
      <td className="px-4 py-3">{r.shipper_name}{r.emailed_at && <div className="text-[10px] text-green-700" data-testid={`receipt-emailed-${r.receipt_number}`}>Emailed {new Date(r.emailed_at).toLocaleDateString()}</div>}</td>
      <td className="px-4 py-3">{r.consignee_name}<div className="text-xs text-slate-500">{r.destination}</div></td>
      <td className="px-4 py-3 font-mono">{r.total_pieces}</td>
      <td className="px-4 py-3 font-mono text-xs">{fmtMoney(r.total_charges, r.currency)}<div className={r.balance_due > 0 ? "text-red-600" : "text-green-700"}>Bal {fmtMoney(r.balance_due, r.currency)}</div></td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <button onClick={onEdit} className={`${btn} text-[#1E2A78]`} data-testid={`receipt-edit-${r.receipt_number}`}><Edit3 className="h-3.5 w-3.5" /> Edit</button>
        <a href={`/admin/receipts/${r.id}/print`} target="_blank" rel="noopener noreferrer" className={`${btn} text-[#1E2A78]`} data-testid={`receipt-print-${r.receipt_number}`}><Printer className="h-3.5 w-3.5" /> Print</a>
        {!r.voided && <EmailReceiptButton receipt={r} onSent={onChanged} className={`${btn} text-[#1E2A78]`} />}
        {tracked ? <span className="text-[10px] uppercase font-semibold text-green-700 mr-3" data-testid={`receipt-tracked-${r.receipt_number}`}>Tracking live</span>
          : !r.voided && <button onClick={() => act(() => api.post("/shipments", shipmentPayload(r)), `Tracking ${r.receipt_number} created`)} className={`${btn} text-green-700`} data-testid={`receipt-track-${r.receipt_number}`}><PackagePlus className="h-3.5 w-3.5" /> Create tracking</button>}
        <button onClick={() => act(() => api.put(`/receipts/${r.id}`, { ...r, voided: !r.voided }), r.voided ? "Receipt restored" : "Receipt voided")} className={`${btn} text-red-600 mr-0`} data-testid={`receipt-void-${r.receipt_number}`}>
          {r.voided ? <><RotateCcw className="h-3.5 w-3.5" /> Restore</> : <><Ban className="h-3.5 w-3.5" /> Void</>}
        </button>
      </td>
    </tr>
  );
}

export function ReceiptsTab({ shipments, reloadShipments }) {
  const [receipts, setReceipts] = useState([]);
  const [next, setNext] = useState(null);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const [r, n] = await Promise.all([api.get("/receipts"), api.get("/receipts/next-number")]);
      setReceipts(r.data); setNext(n.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const tracked = new Set(shipments.map((s) => s.tracking_number));
  const needle = q.trim().toLowerCase();
  const shown = needle ? receipts.filter((r) => [r.receipt_number, r.shipper_name, r.consignee_name].some((v) => v?.toLowerCase().includes(needle))) : receipts;
  const changed = () => { load(); reloadShipments(); };

  return (
    <div className="space-y-4" data-testid="receipts-tab">
      <div className="flex flex-wrap gap-3 justify-between items-center">
        <NextNumberBar next={next} reload={load} />
        <div className="flex gap-2">
          <input placeholder="Search receipt #, shipper, consignee" value={q} onChange={(e) => setQ(e.target.value)} className={`${INP} w-64`} data-testid="receipt-search" />
          <button onClick={() => setEditing({})} className="px-4 py-2 bg-[#FFD700] text-[#1E2A78] font-semibold rounded-sm hover:bg-[#E6C200] inline-flex items-center gap-2 whitespace-nowrap" data-testid="new-receipt-btn"><Plus className="h-4 w-4" /> New Receipt</button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>{["Receipt #", "Date", "Shipper", "Consignee", "Pcs", "Charges"].map((h) => <th key={h} className="px-4 py-3 text-left">{h}</th>)}<th className="px-4 py-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No warehouse receipts yet.</td></tr>}
            {shown.map((r) => <ReceiptRow key={r.id} r={r} tracked={tracked.has(r.receipt_number)} onEdit={() => setEditing(r)} onChanged={changed} />)}
          </tbody>
        </table>
      </div>
      {editing && (
        <ReceiptFormModal receipt={editing.id ? editing : null} nextNumber={next?.preview} onClose={() => setEditing(null)} onSuccess={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}
